import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import './ManualSignalOrderForm.css';
import { buildScopedApiUrl, getApiHeaders } from './config';
import { buildTradeServiceUrl } from './tradeServiceRouting';
import { useWorkspace } from './workspace/WorkspaceContext';

Modal.setAppElement('#root');

const CROSS_HIDDEN_EMA_OPTIONS = ['EMA200_BAND', 'EMA200_BAND_CROSS_TREND_ALIGNED'];

const computePnLPct = (level, price, side) => {
  const lvl = Number.parseFloat(level);
  const p = Number.parseFloat(price);
  if (!Number.isFinite(lvl) || !Number.isFinite(p) || p === 0) return null;

  const raw = ((lvl - p) / p) * 100;
  const orientation = side === 'open_short' ? -1 : 1;
  return raw * orientation;
};

function ManualSignalOrderForm({ isOpen, closeModal }) {
  const { workspace } = useWorkspace();
  const [formData, setFormData] = useState({
    symbol: '',
    side: '',
    orderType: 'market',
    limitPrice: '',
    margin: '10',
    confluences: [],
    presetStopLoss: '',
    presetTakeProfit: '',
    tradingStrategy: 'ICT_MANUAL_TRADE_ENTRY_EXIT',
    tradingTimeFrame: '15',
    activePositionCountLimitCheck: 'REQUIRED',
    takeProfitTracker: '',
    tpTrackerValue: '',
    tpSlTrackerEmaCandleCross: '',
    takeProfitTrackerBase: 'EMA',
    addTpTrackerOpt: 'NO',
    tpSrOpt: 'YES',
    slSrOpt: 'YES',
    tpSrLevel: '',
    slSrLevel: '',
    exchange: workspace.exchange,
    marketType: workspace.marketType,
  });

  const TIMEFRAME_OPTIONS = useMemo(() => [5, 15, 30, 60, 240, 480, 720, 1440], []);
  const TP_BASE_OPTIONS = ['EMA', 'VWAP', 'SWING_HIGH_LOW', 'CANDLE_COLOR', 'RSI_AND_SRSI_REVERSAL', 'PROFIT_PERCENT'];
  const VWAP_OPTIONS = ['DAILY_VWAP', 'WEEKLY_VWAP'];
  const SWING_OPTIONS = ['SWING_HIGH', 'SWING_LOW', 'SWING_HIGH_WICK', 'SWING_LOW_WICK'];
  const CANDLE_COLOR_OPTIONS = ['RED', 'GREEN'];

  const [symbolOptions, setSymbolOptions] = useState([]);
  const [availableTimeFrames, setAvailableTimeFrames] = useState([]);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const defaultLevelsFor = useCallback((side, limitPrice) => {
    const p = Number.parseFloat(limitPrice);
    if (!Number.isFinite(p) || p <= 0) return {};
    const fmt = (n) => n.toFixed(4);
    const up = p * 1.02;
    const dn = p * 0.98;
    if (side === 'open_long') {
      return { tp: fmt(up), sl: fmt(dn) };
    }
    if (side === 'open_short') {
      return { tp: fmt(dn), sl: fmt(up) };
    }
    return {};
  }, []);

  const handleTpBaseChange = (e) => {
    const base = e.target.value;
    setFormData((prev) => ({
      ...prev,
      takeProfitTrackerBase: base,
      takeProfitTracker: base === 'RSI_AND_SRSI_REVERSAL' ? 'RSI_AND_SRSI_REVERSAL' : '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfluencesChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      confluences: e.target.value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    }));
  };

  const handleSymbolChange = (e) => {
    const selectedSymbol = e.target.value;
    setFormData((prev) => ({
      ...prev,
      symbol: selectedSymbol,
      tradingTimeFrame: '',
      tpSrLevel: '',
      slSrLevel: '',
    }));
  };

  const handleTpSrToggle = (yesNo) => {
    setFormData((prev) => {
      const next = { ...prev, tpSrOpt: yesNo };
      if (yesNo === 'YES' && !prev.tpSrLevel) {
        const { tp } = defaultLevelsFor(prev.side, prev.limitPrice);
        if (tp) next.tpSrLevel = tp;
      }
      return next;
    });
  };

  const handleSlSrToggle = (yesNo) => {
    setFormData((prev) => {
      const next = { ...prev, slSrOpt: yesNo };
      if (yesNo === 'YES' && !prev.slSrLevel) {
        const { sl } = defaultLevelsFor(prev.side, prev.limitPrice);
        if (sl) next.slSrLevel = sl;
      }
      return next;
    });
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
    }));
  }, [workspace]);

  useEffect(() => {
    fetch(buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace), {
      headers: getApiHeaders(),
    })
      .then((res) => res.json())
      .then((coins) => {
        const uniq = Array.from(new Set(coins || [])).filter(Boolean);
        setSymbolOptions(uniq);
        setFormData((prev) => ({
          ...prev,
          symbol: uniq.includes(prev.symbol) ? prev.symbol : (uniq[0] || ''),
          tradingTimeFrame: uniq.includes(prev.symbol) ? prev.tradingTimeFrame : '',
          side: uniq.includes(prev.symbol) ? prev.side : '',
        }));
      })
      .catch((err) => console.error('Failed to load symbol options', err));
  }, [workspace]);

  useEffect(() => {
    const fetchMarketPrice = async () => {
      const response = await fetch(
        buildTradeServiceUrl('/api/bot/get/getMarkPriceBySymbol', workspace, {
          symbol: formData.symbol,
        }),
        { headers: getApiHeaders() }
      );
      const data = await response.json();
      setFormData((prev) => {
        const next = { ...prev, limitPrice: data.markPrice };
        const { tp, sl } = defaultLevelsFor(prev.side, data.markPrice);
        if (prev.tpSrOpt === 'YES' && tp) next.tpSrLevel = tp;
        if (prev.slSrOpt === 'YES' && sl) next.slSrLevel = sl;
        return next;
      });
    };

    if (formData.symbol) {
      fetchMarketPrice().catch((err) => console.error('Failed to load mark price', err));
    }
  }, [formData.symbol, defaultLevelsFor, workspace]);

  useEffect(() => {
    setAvailableTimeFrames(TIMEFRAME_OPTIONS);
  }, [TIMEFRAME_OPTIONS]);

  useEffect(() => {
    if (formData.symbol) {
      setFormData((prev) => ({ ...prev, tradingTimeFrame: '' }));
    }
  }, [formData.symbol]);

  useEffect(() => {
    if (!formData.side) return;
    const { tp, sl } = defaultLevelsFor(formData.side, formData.limitPrice);
    setFormData((prev) => {
      let changed = false;
      const next = { ...prev };
      if (prev.tpSrOpt === 'YES' && !prev.tpSrLevel && tp) {
        next.tpSrLevel = tp;
        changed = true;
      }
      if (prev.slSrOpt === 'YES' && !prev.slSrLevel && sl) {
        next.slSrLevel = sl;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [formData.side, formData.limitPrice, formData.tpSrOpt, formData.slSrOpt, defaultLevelsFor]);

  const buildTakeProfitTrackerType = useCallback((base, value, crossMode, side) => {
    if (!base) return '';
    const simpleBases = ['CANDLE_COLOR', 'RSI_AND_SRSI_REVERSAL', 'PROFIT_PERCENT'];
    if (simpleBases.includes(base)) {
      return base;
    }
    if (base === 'EMA' && CROSS_HIDDEN_EMA_OPTIONS.includes(value)) {
      return value;
    }
    if (!value || !crossMode || !side) return '';
    const dir = side === 'open_long' ? 'BEAR' : side === 'open_short' ? 'BULL' : '';
    const crossPrefix = crossMode === 'FIRST_CANDLE_CROSS' ? 'FIRST_CANDLE' : 'CONFIRMED';
    return `${value}_${crossPrefix}_${dir}_CROSS`;
  }, []);

  const composedTpType = useMemo(
    () => buildTakeProfitTrackerType(
      formData.takeProfitTrackerBase,
      formData.takeProfitTracker,
      formData.tpSlTrackerEmaCandleCross,
      formData.side
    ),
    [
      formData.takeProfitTrackerBase,
      formData.takeProfitTracker,
      formData.tpSlTrackerEmaCandleCross,
      formData.side,
      buildTakeProfitTrackerType,
    ]
  );

  const validateTrackerInputs = () => {
    if (formData.addTpTrackerOpt !== 'YES') return true;
    if (!formData.tradingTimeFrame) {
      setError('Please choose Trading Time Frame');
      return false;
    }
    if (!formData.takeProfitTrackerBase) {
      setError('Please choose a Tracker Base');
      return false;
    }
    if (formData.takeProfitTrackerBase === 'PROFIT_PERCENT') {
      if (!formData.tpTrackerValue) {
        setError('Please enter Profit % Threshold');
        return false;
      }
    } else if (!formData.takeProfitTracker) {
      setError('Please choose Take Profit/SL Tracker');
      return false;
    }

    const formationBases = ['EMA', 'VWAP', 'SWING_HIGH_LOW'];
    if (formationBases.includes(formData.takeProfitTrackerBase)) {
      if (!formData.side) {
        setError('Please choose Side for the selected base');
        return false;
      }
      if (!CROSS_HIDDEN_EMA_OPTIONS.includes(formData.takeProfitTracker) && !formData.tpSlTrackerEmaCandleCross) {
        setError('Please choose Candle Cross mode for the selected base');
        return false;
      }
    }

    if (!composedTpType) {
      setError('Unable to derive takeProfitTrackerType. Check Side/Cross/Tracker selections.');
      return false;
    }
    return true;
  };

  const validateSrInputs = () => {
    const wantsTp = formData.tpSrOpt === 'YES';
    const wantsSl = formData.slSrOpt === 'YES';
    if (!wantsTp && !wantsSl) return true;
    if (!formData.side) {
      setError('Please choose Side before saving SR levels.');
      return false;
    }
    if (!formData.tradingTimeFrame) {
      setError('Please choose Trading Time Frame before saving SR levels.');
      return false;
    }
    if (wantsTp && !formData.tpSrLevel) {
      setError('Please enter Take Profit SR Level.');
      return false;
    }
    if (wantsSl && !formData.slSrLevel) {
      setError('Please enter Stop Loss SR Level.');
      return false;
    }
    return true;
  };

  const tpChangePct = useMemo(
    () => computePnLPct(formData.tpSrLevel, formData.limitPrice, formData.side),
    [formData.tpSrLevel, formData.limitPrice, formData.side]
  );
  const slChangePct = useMemo(
    () => computePnLPct(formData.slSrLevel, formData.limitPrice, formData.side),
    [formData.slSrLevel, formData.limitPrice, formData.side]
  );

  const saveSingleTakeProfitTracker = async () => {
    const payload = {
      symbol: formData.symbol,
      tradeSide: formData.side,
      timeFrame: String(formData.tradingTimeFrame),
      takeProfitType: composedTpType,
      takeProfitTrackerStartedOn: new Date().toISOString(),
      expectedCandleColorToClose: '',
      takeProfitTrackerStoppedOn: '',
      activeFlag: 'Y',
      exchange: workspace.exchange,
      marketType: workspace.marketType,
      ...(formData.takeProfitTrackerBase === 'PROFIT_PERCENT' && {
        tpTrackerValue: formData.tpTrackerValue,
      }),
    };

    const res = await fetch(buildScopedApiUrl('/api/bot/ui/get/saveTakeProfitTrackers', workspace), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`SaveTakeProfitTracker failed: ${res.status} ${t}`);
    }
    return res.json();
  };

  const buildSrPayload = (kind) => {
    const comments = kind === 'TP' ? 'TP_SR from Adhoc Order' : 'SL_SR from Adhoc Order';
    const srStrictType = formData.side === 'open_long' ? 'CLOSE_BUY_ONLY' : 'CLOSE_SELL_ONLY';
    const srLevel = kind === 'TP' ? formData.tpSrLevel : formData.slSrLevel;

    return {
      coin: formData.symbol,
      type: 'ICT_MANUAL_TRADE_SR',
      srLevel,
      zoneHigh: '',
      zoneLow: '',
      timeFrame: String(formData.tradingTimeFrame),
      strictFlag: true,
      srStrictType,
      crossModeForEntry: 'FIRST_CANDLE_CROSS',
      crossModeForExit: 'FIRST_CANDLE_CROSS',
      activeFlag: 'Y',
      status: '',
      levelTakenOutOn: null,
      comments,
      attempt: 1,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
    };
  };

  const saveSrLevel = async (payload) => {
    const res = await fetch(buildScopedApiUrl('/api/bot/ui/get/saveSrDzSzLevels', workspace), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Save SR failed: ${res.status} ${t}`);
    }
    return res.json();
  };

  const wasOrderOpened = (resp) => {
    if (!resp || typeof resp !== 'object') return false;
    if (resp.orderId || resp.positionId || resp.tradeId) return true;
    const s = String(resp.status || resp.message || '').toLowerCase();
    if (/(opened|placed|created|executed|success)/i.test(s)) return true;
    if (/(skip|skipp|skipped|limit|reached|failed|fail|error|rejected|not)/i.test(s)) return false;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateTrackerInputs()) return;
    if (!validateSrInputs()) return;

    setLoading(true);
    try {
      const {
        addTpTrackerOpt,
        takeProfitTrackerBase,
        tpTrackerValue,
        takeProfitTracker,
        tpSlTrackerEmaCandleCross,
        tpSrOpt,
        slSrOpt,
        tpSrLevel,
        slSrLevel,
        ...orderPayload
      } = formData;

      const scopedOrderPayload = {
        ...orderPayload,
        exchange: workspace.exchange,
        marketType: workspace.marketType,
      };

      const res = await fetch(buildTradeServiceUrl('/api/bot/ui/post/adhoc-order-service', workspace), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
        body: JSON.stringify(scopedOrderPayload),
      });
      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();

      let result = data;
      const orderOpened = wasOrderOpened(data);

      if (orderOpened) {
        if (formData.addTpTrackerOpt === 'YES') {
          try {
            await saveSingleTakeProfitTracker();
            result = { ...data, takeProfitTracker: 'CREATED' };
          } catch (e2) {
            result = { ...data, takeProfitTracker: `FAILED - ${e2.message}` };
          }
        }

        const wantsTpSr = formData.tpSrOpt === 'YES';
        const wantsSlSr = formData.slSrOpt === 'YES';
        if (wantsTpSr || wantsSlSr) {
          const srResults = {};
          try {
            if (wantsTpSr) {
              await saveSrLevel(buildSrPayload('TP'));
              srResults.tpSr = 'CREATED';
            }
            if (wantsSlSr) {
              await saveSrLevel(buildSrPayload('SL'));
              srResults.slSr = 'CREATED';
            }
          } catch (e3) {
            srResults.error = e3.message;
          }
          result = { ...result, srSaves: srResults };
        }
      } else {
        result = { ...data, note: 'Order not opened; tracker and SR creation skipped.' };
      }

      setSubmissionResult(result);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const trackerDisabled =
    formData.addTpTrackerOpt !== 'YES' ||
    !formData.side ||
    !formData.tradingTimeFrame;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Manual Signal Order Form"
      className="ReactModal__Content"
      overlayClassName="ReactModal__Overlay"
    >
      <button className="modal-close-button" onClick={closeModal}>&times;</button>
      <h2 className="page-title">Submit AdHoc Signal Order</h2>
      <h3>[Manual]</h3>
      <p style={{ marginTop: 0, color: '#666' }}>
        Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong>
      </p>
      {loading && <p className="loading-message">Submitting...</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {submissionResult && <p>Result: {JSON.stringify(submissionResult)}</p>}

      {!loading && !submissionResult && (
        <form className="modal-form" onSubmit={handleSubmit}>
          <label>
            Symbol:
            <select name="symbol" value={formData.symbol} onChange={handleSymbolChange}>
              <option value="" disabled>Select Symbol</option>
              {symbolOptions.map((sym) => (
                <option key={sym} value={sym}>{sym}</option>
              ))}
            </select>
          </label>

          <label>
            Trading Time Frame:
            <select
              name="tradingTimeFrame"
              value={formData.tradingTimeFrame}
              onChange={handleInputChange}
            >
              <option value="" disabled>Select Time Frame</option>
              {availableTimeFrames.map((tf) => (
                <option key={tf} value={String(tf)}>{tf} min</option>
              ))}
            </select>
          </label>

          <label>
            Side:
            <select
              name="side"
              value={formData.side}
              onChange={handleInputChange}
              required
            >
              <option value="" disabled>Select Side</option>
              <option value="open_long">open_long</option>
              <option value="open_short">open_short</option>
            </select>
          </label>

          <label>Order Type:
            <select name="orderType" value={formData.orderType} onChange={handleInputChange}>
              <option value="limit">Limit</option>
              <option value="market">Market</option>
            </select>
          </label>

          <label>Limit Price:
            <input type="number" step="any" name="limitPrice" value={formData.limitPrice} onChange={handleInputChange} />
          </label>

          <label>Margin:
            <input
              type="number"
              name="margin"
              value={formData.margin}
              onChange={handleInputChange}
              list="margin-presets"
              max="2000"
              min="0"
              step="1"
              required
            />
            <datalist id="margin-presets">
              <option value="10" />
              <option value="20" />
              <option value="50" />
              <option value="100" />
              <option value="500" />
              <option value="1000" />
            </datalist>
          </label>

          <div className="radio-field full">
            <div className="field-label">Create Take Profit Tracker after placing order?</div>
            <div className="radio-group">
              <label className="radio">
                <input
                  type="radio"
                  name="addTpTrackerOpt"
                  checked={formData.addTpTrackerOpt === 'NO'}
                  onChange={() => setFormData((prev) => ({ ...prev, addTpTrackerOpt: 'NO' }))}
                />
                <span>No</span>
              </label>
              <label className="radio">
                <input
                  type="radio"
                  name="addTpTrackerOpt"
                  checked={formData.addTpTrackerOpt === 'YES'}
                  onChange={() => setFormData((prev) => ({ ...prev, addTpTrackerOpt: 'YES' }))}
                />
                <span>Yes</span>
              </label>
            </div>
            <div className="form-help">
              Tracker will use <b>Trading Time Frame</b> and the <b>Take Profit/SL Tracker</b> type selected below.
            </div>
          </div>

          <label>Take Profit/SL Tracker Base:
            <select
              name="takeProfitTrackerBase"
              value={formData.takeProfitTrackerBase}
              onChange={handleTpBaseChange}
              disabled={formData.addTpTrackerOpt !== 'YES'}
            >
              {TP_BASE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>

          {formData.takeProfitTrackerBase === 'EMA' && (
            <label>Take Profit/SL Tracker:
              <select
                name="takeProfitTracker"
                value={formData.takeProfitTracker}
                onChange={handleInputChange}
                disabled={trackerDisabled}
              >
                <option value="">Select</option>
                <option value="EMA200_BAND">EMA200_BAND</option>
                <option value="EMA200_BAND_CROSS_TREND_ALIGNED">EMA200_BAND_CROSS_TREND_ALIGNED</option>
                <option value="EMA12">EMA12</option>
                <option value="EMA21">EMA21</option>
                <option value="EMA12EMA21">EMA12EMA21</option>
                <option value="EMA36">EMA36</option>
                <option value="EMA21EMA36">EMA21EMA36</option>
                <option value="EMA50">EMA50</option>
                <option value="EMA100">EMA100</option>
                <option value="EMA200">EMA200</option>
                <option value="ALL_EMA_STACK_TOP_EMA">ALL_EMA_STACK_TOP_EMA</option>
                <option value="ALL_EMA_STACK_BOTTOM_EMA">ALL_EMA_STACK_BOTTOM_EMA</option>
                <option value="LOWER_LENGTH_EMA_STACK_TOP_EMA">LOWER_LENGTH_EMA_STACK_TOP_EMA</option>
                <option value="LOWER_LENGTH_EMA_STACK_BOTTOM_EMA">LOWER_LENGTH_EMA_STACK_BOTTOM_EMA</option>
              </select>
            </label>
          )}

          {formData.takeProfitTrackerBase === 'VWAP' && (
            <label>VWAP Type:
              <select
                name="takeProfitTracker"
                value={formData.takeProfitTracker}
                onChange={handleInputChange}
                disabled={trackerDisabled}
              >
                <option value="">Select</option>
                {VWAP_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
          )}

          {formData.takeProfitTrackerBase === 'SWING_HIGH_LOW' && (
            <label>Swing Type:
              <select
                name="takeProfitTracker"
                value={formData.takeProfitTracker}
                onChange={handleInputChange}
                disabled={trackerDisabled}
              >
                <option value="">Select</option>
                {SWING_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}

          {formData.takeProfitTrackerBase === 'CANDLE_COLOR' && (
            <label>Candle Color:
              <select
                name="takeProfitTracker"
                value={formData.takeProfitTracker}
                onChange={handleInputChange}
                disabled={trackerDisabled}
              >
                <option value="">Select</option>
                {CANDLE_COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          )}

          {formData.takeProfitTrackerBase === 'RSI_AND_SRSI_REVERSAL' && (
            <div style={{ fontSize: 12, color: '#666' }}>
              No additional selection needed. Will use <code>RSI_AND_SRSI_REVERSAL</code>.
            </div>
          )}

          {formData.takeProfitTrackerBase === 'PROFIT_PERCENT' && (
            <label>Profit % Threshold:
              <input
                type="number"
                name="tpTrackerValue"
                value={formData.tpTrackerValue}
                onChange={handleInputChange}
                disabled={trackerDisabled}
                min="0.1"
                step="0.1"
                placeholder="e.g. 5 for 5%"
              />
            </label>
          )}

          <label>Take Profit/SL Tracker Candle Cross:
            <select
              name="tpSlTrackerEmaCandleCross"
              value={formData.tpSlTrackerEmaCandleCross}
              onChange={handleInputChange}
              disabled={
                trackerDisabled ||
                ['CANDLE_COLOR', 'RSI_AND_SRSI_REVERSAL', 'PROFIT_PERCENT'].includes(formData.takeProfitTrackerBase) ||
                (formData.takeProfitTrackerBase === 'EMA' && CROSS_HIDDEN_EMA_OPTIONS.includes(formData.takeProfitTracker))
              }
            >
              <option value="FIRST_CANDLE_CROSS">FIRST_CANDLE_CROSS</option>
              <option value="CONFIRMED_CANDLE_CROSS">CONFIRMED_CANDLE_CROSS</option>
            </select>
            {formData.addTpTrackerOpt === 'YES' && composedTpType && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Will save as <code>{composedTpType}</code>
              </div>
            )}
          </label>

          <div className="full" style={{ marginTop: 12 }}>
            <div className="section-title">
              Save SR Levels {formData.addTpTrackerOpt === 'YES' ? '(after order and tracker)' : '(after order)'}:
            </div>

            <fieldset
              disabled={!formData.side || !formData.tradingTimeFrame}
              className="sr-block full"
            >
              <div style={{ display: 'flex', gap: 32, marginBottom: 8 }}>
                <div>
                  <div className="field-label">Take Profit SR?</div>
                  <div className="radio-group">
                    <label className="radio">
                      <input
                        type="radio"
                        name="tpSrOpt"
                        checked={formData.tpSrOpt === 'NO'}
                        onChange={() => handleTpSrToggle('NO')}
                      />
                      <span>No</span>
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        name="tpSrOpt"
                        checked={formData.tpSrOpt === 'YES'}
                        onChange={() => handleTpSrToggle('YES')}
                      />
                      <span>Yes</span>
                    </label>
                  </div>
                </div>

                <div>
                  <div className="field-label">Stop Loss SR?</div>
                  <div className="radio-group">
                    <label className="radio">
                      <input
                        type="radio"
                        name="slSrOpt"
                        checked={formData.slSrOpt === 'NO'}
                        onChange={() => handleSlSrToggle('NO')}
                      />
                      <span>No</span>
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        name="slSrOpt"
                        checked={formData.slSrOpt === 'YES'}
                        onChange={() => handleSlSrToggle('YES')}
                      />
                      <span>Yes</span>
                    </label>
                  </div>
                </div>
              </div>

              {formData.tpSrOpt === 'YES' && (
                <label>
                  Take Profit SR Level :
                  {tpChangePct !== null && (
                    <span className={`pct-badge ${tpChangePct >= 0 ? 'pct-pos' : 'pct-neg'}`}>
                      ({tpChangePct >= 0 ? '+' : ''}{tpChangePct.toFixed(2)}%)
                    </span>
                  )}
                  <input
                    type="text"
                    name="tpSrLevel"
                    value={formData.tpSrLevel}
                    onChange={handleInputChange}
                    placeholder="e.g., 61234.5"
                  />
                </label>
              )}

              {formData.slSrOpt === 'YES' && (
                <label>
                  Stop Loss SR Level :
                  {slChangePct !== null && (
                    <span className={`pct-badge ${slChangePct >= 0 ? 'pct-pos' : 'pct-neg'}`}>
                      ({slChangePct >= 0 ? '+' : ''}{slChangePct.toFixed(2)}%)
                    </span>
                  )}
                  <input
                    type="text"
                    name="slSrLevel"
                    value={formData.slSrLevel}
                    onChange={handleInputChange}
                    placeholder="e.g., 59876.0"
                  />
                </label>
              )}
            </fieldset>

            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              If both are <b>Yes</b>, two separate SR records will be saved with appropriate comments.
            </div>
          </div>

          <label>Preset Stop Loss:
            <input type="text" name="presetStopLoss" value={formData.presetStopLoss} onChange={handleInputChange} />
          </label>

          <label>Preset Take Profit:
            <input type="text" name="presetTakeProfit" value={formData.presetTakeProfit} onChange={handleInputChange} />
          </label>

          <label>Confluences (comma-separated):
            <input type="text" value={formData.confluences.join(', ')} onChange={handleConfluencesChange} />
          </label>

          <label>Active Positions Count Rule:
            <select name="activePositionCountLimitCheck" value={formData.activePositionCountLimitCheck} onChange={handleInputChange}>
              <option value="REQUIRED">REQUIRED</option>
              <option value="SKIP">SKIP</option>
            </select>
          </label>

          <div className="modal-button-container">
            <button type="submit">Submit</button>
            <button type="button" onClick={closeModal}>Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default ManualSignalOrderForm;
